import {
  useAppInsightsContext,
  useTrackMetric,
} from '@microsoft/applicationinsights-react-js';
import { type GetStaticProps } from 'next';
import Link from 'next/link';

import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../components/page/Layout';
import { SUPPORT_URL } from '../../utils/constants';

const TITLE = 'Leland Experience Guarantee';
const META_DESCRIPTION = 'Leland Experience Guarantee';

const SPACING_CLASS = 'my-4';

const ExperienceGuarantee: NextPageWithLayout = () => {
  const appInsights = useAppInsightsContext();
  useTrackMetric(appInsights, 'ExperienceGuarantee');
  return (
    <div className="container-def px-4 py-8 lg:p-20">
      <h1 className="mb-8 text-5xl font-semibold">{TITLE}</h1>
      <p className={SPACING_CLASS}>
        At Leland, we understand the importance of finding the right coach to
        help you achieve your goals and unlock your potential. That&rsquo;s why
        we&rsquo;ve created the Leland Experience Guarantee to provide you with
        the confidence and peace of mind when booking a Leland coach. This
        guarantee comprises three pillars: top-quality coaches, first hour
        satisfaction, and ongoing support.
      </p>

      <h2 className="subheading-text mt-6">Top-Quality Coaches</h2>

      <p className={SPACING_CLASS}>
        We take pride in the exceptional quality of our coaches. Each coach on
        our platform undergoes a thorough selection process, including a
        detailed application, and skill assessments. This ensures that we only
        offer you the best and most qualified professionals who are dedicated to
        helping you reach your goals. If you meet with your coach and it&rsquo;s
        not the best fit, we are happy to provide additional coach
        recommendations.
      </p>

      <h2 className="subheading-text mt-6">First Hour Satisfaction</h2>

      <p className={SPACING_CLASS}>
        We&rsquo;re confident that you&rsquo;ll have a positive experience with
        our coaches. However, if for any reason you&rsquo;re not satisfied with
        your first coaching session, please submit a support request here within
        seven days of the session. Our team will review your case, and if
        appropriate, we&rsquo;ll reload the time onto your balance and get you
        matched with a new coach. This allows you to try our services with the
        assurance that your satisfaction is our priority. Note that this policy
        only applies to 1:1 coaching — not bootcamps, classes or Leland+.
      </p>

      <h2 className="subheading-text mt-6">Ongoing Support</h2>

      <p className={SPACING_CLASS}>
        Our commitment to your success goes beyond the Leland Experience
        Guarantee. We&rsquo;re here to support you throughout your coaching
        journey. If you have any concerns or questions about your coaching
        sessions or need assistance in addressing any issues, feel free to{' '}
        <a
          className="link text-leland-gray-light underline hover:text-leland-gray-extra-light"
          href={SUPPORT_URL}
          id="experience_guarantee--submit_support_request"
        >
          submit a support request here
        </a>
        .
      </p>
      <p className={SPACING_CLASS}>
        With the Leland Experience Guarantee, you can feel confident in your
        decision to work with one of our outstanding coaches, knowing that
        we&rsquo;re dedicated to your success and satisfaction. We&rsquo;re
        excited to help you achieve your most ambitious goals.
      </p>
      <p className={SPACING_CLASS}>
        If you have questions about Leland&rsquo;s Refund Policy or Expiration
        Terms outside of this Experience Guarantee, you can read more about that{' '}
        <Link
          className="link text-leland-gray-light underline hover:text-leland-gray-extra-light"
          href="/legal/refund-policy"
          id="experience_guarantee--refund_policy"
        >
          here
        </Link>
        .
      </p>
    </div>
  );
};

ExperienceGuarantee.getLayout = (page, { pageContentData }) => (
  <Layout
    title={TITLE}
    description={META_DESCRIPTION}
    canonicalLink="/legal/experience-guarantee"
    pageContentData={pageContentData}
  >
    {page}
  </Layout>
);

export const getStaticProps: GetStaticProps<{
  pageContentData: Possible<LayoutDataProps>;
}> = async () => {
  const layoutCategoriesHeaderData = await getStaticLayoutQuery();

  return { props: { pageContentData: layoutCategoriesHeaderData } };
};

export default ExperienceGuarantee;
